import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, TextField, Typography } from "@mui/material";
import { Save } from "@mui/icons-material";

const ChangeUserDataForm = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;
    const emailRegex = useMemo(() => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, []);
    const phoneRegex = useMemo(() => /^\(\d{2}\) \d{4,5}-\d{4}$/, []);

    const [isLoading, setIsLoading] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");

    const [validate, setValidate] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const handleUserData = useCallback(async () => {
        setIsLoading(true);

        const response = await fetch(`${backendEndpoint}/me/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const responseBody = await response.json();

        setEmail(responseBody.email);
        setCompanyName(responseBody.companyName);
        setPhoneNumber(responseBody.phoneNumber);
        setIsLoading(false);
    }, [backendEndpoint, getCookieValue]);

    const clearValidations = () => {
        setValidate(false);
        setErrorMessage("");
    };

    const formatPhone = (value) => {
        value = value.replace(/\D/g, "");
        if (value.length > 0 && value.length < 3) {
            value = value.replace(/^(\d{1,2})/, "($1");
        } else if (value.length < 7) {
            value = value.replace(/^(\d{2})(\d{0,4})/, "($1) $2");
        } else if (value.length < 11) {
            value = value.replace(/^(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
        } else {
            value = value.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
        }
        return value;
    };

    const handleUpdateProfile = () => {
        setValidate(true);
        if (
            !companyName ||
            !emailRegex.test(email) ||
            !phoneRegex.test(phoneNumber) ||
            !password
        )
            return;

        setIsLoading(true);

        fetch(`${backendEndpoint}/user/update_profile/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
            body: JSON.stringify({
                companyName,
                phoneNumber,
                email,
                password,
            }),
        })
            .then(async (response) => {
                if (response.ok) {
                    const result = await response.json();
                    return result;
                } else {
                    const result = await response.json();
                    throw new Error(result.detail);
                }
            })
            .then(() => {
                setIsLoading(false);
                setPassword("");
                clearValidations();
            })
            .catch((error) => {
                setErrorMessage(`${error}`.replace("Error: ", ""));
                setIsLoading(false);
            });
    };

    useEffect(() => {
        handleUserData();
    }, [handleUserData]);

    return (
        <>
            <TextField
                value={companyName}
                onChange={(e) => {
                    setCompanyName(e.target.value);
                    clearValidations();
                }}
                label="Nome da empresa*"
                variant="outlined"
                fullWidth
                margin="normal"
                error={validate && companyName === "" ? true : false}
                helperText={
                    validate && companyName === ""
                        ? "Preencha o nome da sua empresa"
                        : ""
                }
            />

            <TextField
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value);
                    clearValidations();
                }}
                label="E-mail*"
                variant="outlined"
                fullWidth
                margin="normal"
                error={validate && !emailRegex.test(email) ? true : false}
                helperText={
                    validate && !emailRegex.test(email)
                        ? "Preencha com um e-mail válido"
                        : ""
                }
            />

            <TextField
                value={phoneNumber}
                onChange={(e) => {
                    setPhoneNumber(formatPhone(e.target.value));
                    clearValidations();
                }}
                label="Número de WhatsApp*"
                variant="outlined"
                fullWidth
                margin="normal"
                error={validate && !phoneRegex.test(phoneNumber) ? true : false}
                helperText={
                    validate && !phoneRegex.test(phoneNumber)
                        ? "Preencha com um número de WhatsApp válido"
                        : ""
                }
            />

            <TextField
                value={password}
                onChange={(e) => {
                    setPassword(e.target.value);
                    clearValidations();
                }}
                label="Confirme com sua senha*"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                error={validate && password === "" ? true : false}
                helperText={
                    validate && password === ""
                        ? "Para salvar as alterações, confirme com sua senha"
                        : ""
                }
                autoComplete="new-password"
            />

            {errorMessage ? (
                <Typography variant="h6">{errorMessage}</Typography>
            ) : null}

            <Button
                variant="contained"
                color="primary"
                sx={{
                    marginBottom: 2,
                    marginTop: 2,
                    borderRadius: "25px",
                    padding: "8px 40px 6px",
                    textTransform: "none",
                    float: "right",
                }}
                disabled={isLoading}
                startIcon={
                    isLoading ? (
                        <CircularProgress size="16px" color="inherit" />
                    ) : (
                        <Save />
                    )
                }
                onClick={handleUpdateProfile}
            >
                Salvar
            </Button>
        </>
    );
};

export default ChangeUserDataForm;
